import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, 
  Plus, Search, Trash2, Eye, EyeOff, CheckSquare, Square, AlertTriangle,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';

const UserManagement = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, data: null });
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'tutor'
  });

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
  ];

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users);
      setFilteredUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [api]);

  useEffect(() => {
    let filtered = users;
    if (roleFilter !== 'all') filtered = filtered.filter(u => u.role === roleFilter);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.first_name?.toLowerCase().includes(term) ||
        u.last_name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term)
      );
    }
    setFilteredUsers(filtered);
  }, [users, roleFilter, searchTerm]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/create-user', newUser);
      toast.success(`${newUser.role.charAt(0).toUpperCase() + newUser.role.slice(1)} created successfully!`);
      setCreateDialogOpen(false);
      setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'tutor' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = (userId, userName) => {
    setConfirmDialog({ open: true, type: 'single', data: { userId, userName } });
  };

  const confirmSingleDelete = async () => {
    const { userId } = confirmDialog.data;
    setConfirmDialog({ open: false, type: null, data: null });
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User and all related data deleted');
      setSelectedIds(prev => { const next = new Set(prev); next.delete(userId); return next; });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const nonAdminFiltered = filteredUsers.filter(u => u.role !== 'admin');
    if (selectedIds.size === nonAdminFiltered.length && nonAdminFiltered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(nonAdminFiltered.map(u => u.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmDialog({ open: true, type: 'bulk', data: { count: selectedIds.size } });
  };

  const confirmBulkDelete = async () => {
    setConfirmDialog({ open: false, type: null, data: null });
    setBulkDeleting(true);
    try {
      const res = await api.post('/admin/users/bulk-delete', { user_ids: Array.from(selectedIds) });
      const { deleted, skipped } = res.data;
      toast.success(`Deleted ${deleted.length} user(s)${skipped.length ? `, ${skipped.length} skipped` : ''}`);
      setSelectedIds(new Set());
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const nonAdminFiltered = filteredUsers.filter(u => u.role !== 'admin');
  const allNonAdminSelected = nonAdminFiltered.length > 0 && selectedIds.size === nonAdminFiltered.length;

  return (
    <ProLayout title="User Management" sidebarItems={sidebarItems}>
      <div data-testid="user-management">
        {/* Header Actions */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-users" />
          </div>
          
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]" data-testid="role-filter">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="tutor">Tutor</SelectItem>
              <SelectItem value="parent">Parent</SelectItem>
              <SelectItem value="student">Student</SelectItem>
            </SelectContent>
          </Select>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" /> Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input id="first_name" value={newUser.first_name} onChange={(e) => setNewUser({...newUser, first_name: e.target.value})} required data-testid="create-first-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input id="last_name" value={newUser.last_name} onChange={(e) => setNewUser({...newUser, last_name: e.target.value})} required data-testid="create-last-name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required data-testid="create-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required className="pr-10" data-testid="create-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={newUser.role} onValueChange={(val) => setNewUser({...newUser, role: val})}>
                    <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tutor">Tutor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Only tutors and admins can be created directly</p>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-create-user">Create User</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div data-testid="bulk-actions-bar" className="flex items-center gap-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-sm font-medium text-red-800">
              {selectedIds.size} user{selectedIds.size > 1 ? 's' : ''} selected
            </span>
            <Button
              data-testid="bulk-delete-btn"
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="ml-auto"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.size})`}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="clear-selection-btn">
              Clear
            </Button>
          </div>
        )}

        {/* Users Table */}
        <div className="pro-card">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-10">
                      <button onClick={toggleSelectAll} className="flex items-center justify-center" data-testid="select-all-checkbox">
                        {allNonAdminSelected ? <CheckSquare className="w-5 h-5 text-emerald-600" /> : <Square className="w-5 h-5 text-slate-400" />}
                      </button>
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Details</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-8 text-slate-500">No users found</td></tr>
                  ) : (
                    filteredUsers.map((user) => {
                      const isAdmin = user.role === 'admin';
                      const isSelected = selectedIds.has(user.id);
                      return (
                        <tr key={user.id} className={isSelected ? 'bg-red-50/50' : ''}>
                          <td>
                            {!isAdmin && (
                              <button onClick={() => toggleSelect(user.id)} data-testid={`select-user-${user.id}`}>
                                {isSelected ? <CheckSquare className="w-5 h-5 text-emerald-600" /> : <Square className="w-5 h-5 text-slate-300" />}
                              </button>
                            )}
                          </td>
                          <td className="font-medium text-slate-800">{user.first_name} {user.last_name}</td>
                          <td className="text-slate-600">{user.email}</td>
                          <td>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize
                              ${user.role === 'admin' ? 'bg-red-100 text-red-700' : ''}
                              ${user.role === 'tutor' ? 'bg-purple-100 text-purple-700' : ''}
                              ${user.role === 'parent' ? 'bg-blue-100 text-blue-700' : ''}
                              ${user.role === 'student' ? 'bg-green-100 text-green-700' : ''}
                            `}>
                              {user.role}
                            </span>
                          </td>
                          <td className="text-sm text-slate-500">
                            {user.role === 'student' && <span>Year {user.year_group} {user.key_stage && `\u2022 ${user.key_stage.toUpperCase()}`}</span>}
                            {user.role === 'tutor' && <span>{user.assigned_students?.length || 0} students</span>}
                            {user.role === 'parent' && <span>{user.children?.length || 0} children</span>}
                          </td>
                          <td className="text-slate-500 text-sm">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                          <td>
                            {!isAdmin && (
                              <div className="flex items-center gap-1">
                                {user.role === 'student' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    onClick={() => navigate(`/admin/parent-letter/${user.id}`)}
                                    title="Print Parent Login Letter"
                                    data-testid={`print-letter-${user.id}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, data: null })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" /> Confirm Delete
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {confirmDialog.type === 'single' && (
                <p className="text-slate-700">
                  Are you sure you want to delete <strong>{confirmDialog.data?.userName}</strong>? This will permanently remove the user and all their related data (progress, lessons, payments, enrollments).
                </p>
              )}
              {confirmDialog.type === 'bulk' && (
                <p className="text-slate-700">
                  Are you sure you want to delete <strong>{confirmDialog.data?.count} user(s)</strong>? This will permanently remove all selected users and their related data (progress, lessons, payments, enrollments). Admin accounts will be skipped.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, type: null, data: null })} data-testid="cancel-delete-btn">Cancel</Button>
              <Button
                variant="destructive"
                onClick={confirmDialog.type === 'single' ? confirmSingleDelete : confirmBulkDelete}
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Permanently
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ProLayout>
  );
};

export default UserManagement;
